import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { fmtDateTime } from "@/components/EntityHistory";
import { SocialConnect } from "@/components/social/SocialConnect";
import { SocialKpis } from "@/components/social/SocialKpis";
import { SocialTopMedia } from "@/components/social/SocialTopMedia";
import { SocialInsights, SocialAudience } from "@/components/social/SocialInsights";
import { SocialMonthly } from "@/components/social/SocialMonthly";
import { SocialAds } from "@/components/social/SocialAds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";

const Header = ({ s, days, setDays, source, setSource, view, setView, isOwner }) => {
  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: async () => (await api.post("/social/sync")).data,
    onSuccess: (r) => { toast.success(r.running ? "Aggiornamento già in corso" : "Aggiornamento avviato: ricarica tra qualche minuto"); setTimeout(() => qc.invalidateQueries({ queryKey: ["social"] }), 20000); },
  });
  return (
    <Card className="mb-6 flex flex-wrap items-center justify-between gap-4 p-5" data-testid="social-header">
      <div className="flex items-center gap-3">
        {s.picture && <img src={s.picture} alt="" className="h-12 w-12 rounded-full object-cover" />}
        <div>
          <p className="font-semibold" data-testid="social-username">@{s.ig_username} · {s.page_name}</p>
          <p className="text-xs text-muted-foreground">Instagram {num(s.followers)} follower{s.fb_followers ? ` · Facebook ${num(s.fb_followers)} follower` : ""} · {s.last_sync ? `aggiornato ${fmtDateTime(s.last_sync)}` : "primo aggiornamento in corso…"}</p>
          {s.status === "error" && <p className="text-xs text-rose-400" data-testid="social-error">Ultimo aggiornamento non riuscito: {s.last_error}</p>}
          {s.status === "partial" && <p className="text-xs text-amber-400" data-testid="social-partial">Aggiornato, ma alcuni dati non sono arrivati (riprovo al prossimo aggiornamento): {s.last_error}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="flex rounded-full border border-border p-0.5" data-testid="social-source">
          {[["ig", "Instagram"], ["fb", "Facebook"], ...(isOwner ? [["ads", "Sponsorizzate"]] : [])].map(([k, l]) => (
            <Button key={k} size="sm" variant={(view === "ads" ? "ads" : source) === k ? "default" : "ghost"} className="h-7 rounded-full" data-testid={`social-source-${k}`}
              onClick={() => (k === "ads" ? setView("ads") : (setView("organic"), setSource(k)))}>{l}</Button>
          ))}
        </div>
        {[[7, "7 giorni"], [30, "30 giorni"], [90, "90 giorni"], [365, "12 mesi"], [730, "24 mesi"]].map(([d, l]) => <Button key={d} size="sm" variant={days === d ? "default" : "outline"} className="h-8 rounded-full" onClick={() => setDays(d)} data-testid={`social-days-${d}`}>{l}</Button>)}
        <Button size="sm" variant="outline" className="h-8" onClick={() => sync.mutate()} disabled={sync.isPending} data-testid="social-sync-btn"><RefreshCw className="mr-2 h-3.5 w-3.5" />Aggiorna ora</Button>
      </div>
    </Card>
  );
};

const ReachChart = ({ series, days }) => {
  const expected = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const short = series.length && series[0].date > expected;
  return (
  <Card className="mb-6 p-5" data-testid="social-chart">
    <p className="mb-3 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Account raggiunti e visualizzazioni al giorno · ultimi {days} giorni</p>
    {short && <p className="mb-2 text-xs text-amber-400" data-testid="social-chart-short">Dati giornalieri disponibili dal {series[0].date.split("-").reverse().join("/")}: sto recuperando i giorni precedenti, ricarica tra qualche minuto.</p>}
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={series}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="date" tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)} fontSize={11} />
        <YAxis fontSize={11} width={45} />
        <Tooltip formatter={(v, n) => [num(v), n === "reach" ? "Raggiunti" : "Visualizzazioni"]} />
        <Area type="monotone" dataKey="views" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground))" fillOpacity={0.1} />
        <Area type="monotone" dataKey="reach" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.25} />
      </AreaChart>
    </ResponsiveContainer>
  </Card>
  );
};

const Dashboard = ({ s, isOwner }) => {
  const [days, setDays] = useState(30);
  const [source, setSource] = useState("ig");
  const [view, setView] = useState("organic");
  const [reconnect, setReconnect] = useState(false);
  const { data } = useQuery({ queryKey: ["social", "summary", days, source], queryFn: async () => (await api.get("/social/summary", { params: { days, source } })).data, placeholderData: keepPreviousData });
  return (
    <>
      <Header s={s} days={days} setDays={setDays} source={source} setSource={setSource} view={view} setView={setView} isOwner={isOwner} />
      {view === "ads" ? (
        s.ads_connected && !reconnect ? <SocialAds days={days} /> : (
          <div className="space-y-3" data-testid="social-ads-reconnect">
            <p className="text-sm text-amber-400">Per leggere le sponsorizzate incolla di nuovo il token dell'utente di sistema (lo stesso di prima va bene): serve a leggere l'account pubblicitario.</p>
            <SocialConnect isOwner={isOwner} />
          </div>
        )
      ) : !data ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="space-y-6">
          {days > 90 ? <SocialMonthly months={days === 365 ? 12 : 24} source={source} /> : <>
            <SocialKpis kpis={data.kpis} source={source} />
            {data.series.length > 1 && <ReachChart series={data.series} days={days} />}
          </>}
          <SocialInsights media={data.media} />
          <SocialTopMedia media={data.media} />
          {source === "ig" && <SocialAudience demo={data.demographics} />}
        </div>
      )}
      {view === "ads" && s.ads_connected && isOwner && (
        <Button variant="link" size="sm" className="mt-2 px-0 text-xs text-muted-foreground" onClick={() => setReconnect(!reconnect)} data-testid="social-ads-token-btn">{reconnect ? "Annulla" : "Aggiorna il token"}</Button>
      )}
    </>
  );
};

export default function Social() {
  const isOwner = useAuth()?.user?.role === "admin";
  const { data: s, isLoading } = useQuery({ queryKey: ["social", "status"], queryFn: async () => (await api.get("/social/status")).data, refetchInterval: (q) => (q.state.data?.connected && !q.state.data?.last_sync ? 15000 : false) });
  return (
    <div data-testid="social-page">
      <PageHeader title="Social" subtitle="Instagram e Facebook: copertura, contenuti migliori e pubblico, aggiornati ogni giorno." />
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" />
        : !s.connected || s.status === "reauth" ? <SocialConnect isOwner={isOwner} reauth={s.status === "reauth"} />
        : <Dashboard s={s} isOwner={isOwner} />}
    </div>
  );
}
